import { useEffect, useState, useMemo } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useMarket, filterByPortals } from '../../contexts/MarketContext';
import { TOUR_CATEGORIES, CATEGORY_LABELS, CATEGORY_SHORT, CATEGORY_COLORS } from '../../utils/statuses';
import useStatusPipeline from '../../hooks/useStatusPipeline';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function tourName(r) {
  return r.tourName || `${r.sport} — ${r.destination}`;
}

export default function StatusPipelineOverview() {
  const navigate = useNavigate();
  const { portals } = useMarket();
  const { stagesFor } = useStatusPipeline();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'requests'), where('archived', '==', false));
    const unsub = onSnapshot(q, (snap) => {
      setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => !r.crmOrigin || r.crmFullyConverted));
      setLoading(false);
    });
    return unsub;
  }, []);

  const rows = useMemo(() => {
    return filterByPortals(requests, portals)
      .filter((r) => r.departureDate)
      .filter((row) => {
        if (!search.trim()) return true;
        const term = search.trim().toLowerCase();
        return tourName(row).toLowerCase().includes(term) || (row.destination || '').toLowerCase().includes(term);
      })
      .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''));
  }, [requests, portals, search]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">Status Pipeline Overview</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">Every tour and the stage of the pipeline it's currently at, in one view.</p>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tours…"
          className="w-[220px] border border-[#E5E7EB] rounded-[9px] px-3 py-[7px] text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-pb"
        />
      </div>

      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#F1F3F6] bg-surface">
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-[0.7px] sticky left-0 bg-surface">Tour</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-[0.7px]">Departure</th>
                {TOUR_CATEGORIES.map((cat) => (
                  <th key={cat} className="px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-[0.7px] text-center whitespace-nowrap">
                    {CATEGORY_SHORT[cat]}
                  </th>
                ))}
                <th className="px-4 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-[0.7px] text-center">Cancelled</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={TOUR_CATEGORIES.length + 3} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={TOUR_CATEGORIES.length + 3} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">No tours with a departure date yet.</td></tr>
              ) : (
                rows.map((r) => {
                  const cat = r.category || 'sales_admin';
                  const catIdx = TOUR_CATEGORIES.indexOf(cat);
                  const cancelled = cat === 'cancelled';
                  return (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/staff/requests/${r.id}`)}
                      className="border-b border-[#F1F3F6] last:border-0 hover:bg-[#F9FAFB] cursor-pointer transition"
                    >
                      <td className="px-5 py-3 sticky left-0 bg-white">
                        <p className="text-[13.5px] font-semibold text-[#111827] whitespace-nowrap">{tourName(r)}</p>
                      </td>
                      <td className="px-5 py-3 text-[13px] text-[#374151] tabular-nums whitespace-nowrap">{formatDate(r.departureDate)}</td>
                      {TOUR_CATEGORIES.map((stageCat, i) => {
                        if (cancelled) return <td key={stageCat} className="px-4 py-3 text-center text-[#E5E7EB]">—</td>;
                        const done = i < catIdx;
                        const current = i === catIdx;
                        const stages = stagesFor(stageCat, r);
                        const tickedCount = current ? stages.filter((s) => !!r.stageProgress?.[s.key]).length : 0;
                        return (
                          <td key={stageCat} className="px-4 py-3 text-center">
                            {done ? (
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-[12px] font-bold" style={{ background: CATEGORY_COLORS[stageCat] }}>✓</span>
                            ) : current ? (
                              <span
                                className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[11px] font-bold text-white whitespace-nowrap"
                                style={{ background: CATEGORY_COLORS[stageCat] }}
                                title={CATEGORY_LABELS[stageCat]}
                              >
                                {stages.length > 0 ? `${tickedCount}/${stages.length}` : 'Current'}
                              </span>
                            ) : (
                              <span className="inline-block w-2 h-2 rounded-full bg-[#E5E7EB]" />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center">
                        {cancelled && (
                          <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[11px] font-bold text-white bg-dr">Cancelled</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
